import * as types from '../mutation-types';
import InternalChatAPI from '../../api/internalChat';

export const state = {
  chats: {},
  messages: {},
  activeChat: null,
  uiFlags: {
    isFetchingChats: false,
    isFetchingMessages: false,
    isCreatingChat: false,
    isSendingMessage: false,
  },
  totalUnreadCount: 0,
};

export const getters = {
  getChats: $state => {
    return Object.values($state.chats).sort(
      (a, b) => (b.last_activity_at || 0) - (a.last_activity_at || 0)
    );
  },

  getChatById: $state => id => {
    return $state.chats[id] || null;
  },

  getActiveChat: $state => {
    return $state.activeChat ? $state.chats[$state.activeChat] : null;
  },

  getMessages: $state => chatId => {
    const chatMessages = $state.messages[chatId] || [];
    return [...chatMessages].sort((a, b) => a.created_at - b.created_at);
  },

  getUIFlags: $state => {
    return $state.uiFlags;
  },

  getTotalUnreadCount: $state => {
    return $state.totalUnreadCount;
  },

  getUnreadCountForChat: $state => chatId => {
    const chat = $state.chats[chatId];
    return chat ? chat.unread_count || 0 : 0;
  },
};

export const actions = {
  // Busca todos os chats
  fetchChats: async ({ commit }) => {
    commit(types.default.SET_INTERNAL_CHAT_UI_FLAG, {
      isFetchingChats: true,
    });
    try {
      const response = await InternalChatAPI.getChats();
      commit(types.default.SET_INTERNAL_CHATS, response.data);
      commit(types.default.UPDATE_INTERNAL_CHAT_UNREAD_COUNT);
    } catch (error) {
      console.error('Error fetching internal chats:', error);
    } finally {
      commit(types.default.SET_INTERNAL_CHAT_UI_FLAG, {
        isFetchingChats: false,
      });
    }
  },

  // Busca detalhes de um chat
  fetchChat: async ({ commit }, chatId) => {
    try {
      const response = await InternalChatAPI.getChat(chatId);
      commit(types.default.UPDATE_INTERNAL_CHAT, response.data);
    } catch (error) {
      console.error('Error fetching internal chat:', error);
    }
  },

  // Cria chat em grupo
  createGroupChat: async (
    { commit },
    { name, description, participantIds }
  ) => {
    commit(types.default.SET_INTERNAL_CHAT_UI_FLAG, { isCreatingChat: true });
    try {
      const response = await InternalChatAPI.createGroupChat({
        name,
        description,
        participantIds,
      });
      commit(types.default.ADD_INTERNAL_CHAT, response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating group chat:', error);
      throw error;
    } finally {
      commit(types.default.SET_INTERNAL_CHAT_UI_FLAG, {
        isCreatingChat: false,
      });
    }
  },

  // Cria chat direto
  createDirectChat: async ({ commit }, userId) => {
    commit(types.default.SET_INTERNAL_CHAT_UI_FLAG, { isCreatingChat: true });
    try {
      const response = await InternalChatAPI.createDirectChat(userId);
      commit(types.default.ADD_INTERNAL_CHAT, response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating direct chat:', error);
      throw error;
    } finally {
      commit(types.default.SET_INTERNAL_CHAT_UI_FLAG, {
        isCreatingChat: false,
      });
    }
  },

  // Define chat ativo
  setActiveChat: ({ commit }, chatId) => {
    commit(types.default.SET_ACTIVE_INTERNAL_CHAT, chatId);
  },

  // Busca mensagens
  fetchMessages: async ({ commit }, { chatId, before, after, limit }) => {
    commit(types.default.SET_INTERNAL_CHAT_UI_FLAG, {
      isFetchingMessages: true,
    });
    try {
      const response = await InternalChatAPI.getMessages(chatId, {
        before,
        after,
        limit,
      });
      commit(types.default.SET_INTERNAL_CHAT_MESSAGES, {
        chatId,
        messages: response.data,
        prepend: !!before,
      });
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      commit(types.default.SET_INTERNAL_CHAT_UI_FLAG, {
        isFetchingMessages: false,
      });
    }
  },

  // Envia mensagem
  sendMessage: async ({ commit }, { chatId, content, replyToId }) => {
    commit(types.default.SET_INTERNAL_CHAT_UI_FLAG, { isSendingMessage: true });
    try {
      const response = await InternalChatAPI.sendMessage(chatId, {
        content,
        replyToId,
      });
      commit(types.default.ADD_INTERNAL_CHAT_MESSAGE, {
        chatId,
        message: response.data,
      });
      commit(types.default.UPDATE_INTERNAL_CHAT_ACTIVITY, {
        chatId,
        lastMessage: response.data,
      });
      return response.data;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    } finally {
      commit(types.default.SET_INTERNAL_CHAT_UI_FLAG, {
        isSendingMessage: false,
      });
    }
  },

  // Envia mensagem com anexo
  sendMessageWithAttachments: async ({ commit }, { chatId, formData }) => {
    commit(types.default.SET_INTERNAL_CHAT_UI_FLAG, { isSendingMessage: true });
    try {
      const response = await InternalChatAPI.sendMessageWithAttachments(
        chatId,
        formData
      );
      commit(types.default.ADD_INTERNAL_CHAT_MESSAGE, {
        chatId,
        message: response.data,
      });
      commit(types.default.UPDATE_INTERNAL_CHAT_ACTIVITY, {
        chatId,
        lastMessage: response.data,
      });
      return response.data;
    } catch (error) {
      console.error('Error sending message with attachments:', error);
      throw error;
    } finally {
      commit(types.default.SET_INTERNAL_CHAT_UI_FLAG, {
        isSendingMessage: false,
      });
    }
  },

  // Marca chat como lido
  markAsRead: async ({ commit }, chatId) => {
    try {
      await InternalChatAPI.markAsRead(chatId);
      commit(types.default.MARK_INTERNAL_CHAT_AS_READ, chatId);
      commit(types.default.UPDATE_INTERNAL_CHAT_UNREAD_COUNT);
    } catch (error) {
      console.error('Error marking chat as read:', error);
    }
  },

  // Adiciona mensagem recebida via WebSocket
  addMessageFromSocket: ({ commit, state: $state }, { chatId, message }) => {
    commit(types.default.ADD_INTERNAL_CHAT_MESSAGE, { chatId, message });
    commit(types.default.UPDATE_INTERNAL_CHAT_ACTIVITY, {
      chatId,
      lastMessage: message,
    });

    // Se não é o chat ativo, incrementa contador
    if ($state.activeChat !== chatId) {
      commit(types.default.INCREMENT_INTERNAL_CHAT_UNREAD, chatId);
      commit(types.default.UPDATE_INTERNAL_CHAT_UNREAD_COUNT);
    }
  },

  // Remove mensagem (via API)
  deleteMessage: async ({ commit }, { chatId, messageId }) => {
    try {
      await InternalChatAPI.deleteMessage(chatId, messageId);
      commit(types.default.DELETE_INTERNAL_CHAT_MESSAGE, { chatId, messageId });
    } catch (error) {
      console.error('Error deleting message:', error);
      throw error;
    }
  },

  // Remove mensagem recebida via WebSocket (sem chamar API)
  removeMessageFromSocket: ({ commit }, { chatId, messageId }) => {
    commit(types.default.DELETE_INTERNAL_CHAT_MESSAGE, { chatId, messageId });
  },

  // Adiciona participante
  addParticipant: async (_, { chatId, userId, isAdmin }) => {
    try {
      await InternalChatAPI.addParticipant(chatId, userId, isAdmin);
    } catch (error) {
      console.error('Error adding participant:', error);
      throw error;
    }
  },

  // Remove participante
  removeParticipant: async (_, { chatId, userId }) => {
    try {
      await InternalChatAPI.removeParticipant(chatId, userId);
    } catch (error) {
      console.error('Error removing participant:', error);
      throw error;
    }
  },

  // Deleta chat
  deleteChat: async ({ commit }, chatId) => {
    try {
      await InternalChatAPI.deleteChat(chatId);
      commit(types.default.DELETE_INTERNAL_CHAT, chatId);
    } catch (error) {
      console.error('Error deleting chat:', error);
      throw error;
    }
  },
};

export const mutations = {
  [types.default.SET_INTERNAL_CHAT_UI_FLAG]($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },

  [types.default.SET_INTERNAL_CHATS]($state, chats) {
    const chatsMap = {};
    chats.forEach(chat => {
      chatsMap[chat.id] = chat;
    });
    $state.chats = chatsMap;
  },

  [types.default.ADD_INTERNAL_CHAT]($state, chat) {
    $state.chats[chat.id] = chat;
  },

  [types.default.UPDATE_INTERNAL_CHAT]($state, chat) {
    if ($state.chats[chat.id]) {
      $state.chats[chat.id] = { ...$state.chats[chat.id], ...chat };
    } else {
      $state.chats[chat.id] = chat;
    }
  },

  [types.default.DELETE_INTERNAL_CHAT]($state, chatId) {
    delete $state.chats[chatId];
    delete $state.messages[chatId];
    if ($state.activeChat === chatId) {
      $state.activeChat = null;
    }
  },

  [types.default.SET_ACTIVE_INTERNAL_CHAT]($state, chatId) {
    $state.activeChat = chatId;
  },

  [types.default.SET_INTERNAL_CHAT_MESSAGES](
    $state,
    { chatId, messages, prepend }
  ) {
    const existingMessages = $state.messages[chatId] || [];
    if (prepend) {
      $state.messages[chatId] = [...messages, ...existingMessages];
    } else {
      $state.messages[chatId] = messages;
    }
  },

  [types.default.ADD_INTERNAL_CHAT_MESSAGE]($state, { chatId, message }) {
    const messages = $state.messages[chatId] || [];
    // Evita duplicatas
    if (!messages.find(m => m.id === message.id)) {
      $state.messages[chatId] = [...messages, message];
    }
  },

  [types.default.DELETE_INTERNAL_CHAT_MESSAGE]($state, { chatId, messageId }) {
    const messages = $state.messages[chatId] || [];
    $state.messages[chatId] = messages.filter(m => m.id !== messageId);
  },

  [types.default.UPDATE_INTERNAL_CHAT_ACTIVITY](
    $state,
    { chatId, lastMessage }
  ) {
    if ($state.chats[chatId]) {
      $state.chats[chatId] = {
        ...$state.chats[chatId],
        last_activity_at: lastMessage.created_at,
        last_message: {
          id: lastMessage.id,
          content: lastMessage.content,
          sender_name: lastMessage.sender?.name,
          created_at: lastMessage.created_at,
          message_type: lastMessage.message_type,
        },
      };
    }
  },

  [types.default.MARK_INTERNAL_CHAT_AS_READ]($state, chatId) {
    if ($state.chats[chatId]) {
      $state.chats[chatId] = {
        ...$state.chats[chatId],
        unread_count: 0,
      };
    }
  },

  [types.default.INCREMENT_INTERNAL_CHAT_UNREAD]($state, chatId) {
    if ($state.chats[chatId]) {
      const currentCount = $state.chats[chatId].unread_count || 0;
      $state.chats[chatId] = {
        ...$state.chats[chatId],
        unread_count: currentCount + 1,
      };
    }
  },

  [types.default.UPDATE_INTERNAL_CHAT_UNREAD_COUNT]($state) {
    $state.totalUnreadCount = Object.values($state.chats).reduce(
      (total, chat) => total + (chat.unread_count || 0),
      0
    );
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
